import { WalletListSelector } from "@/app/(v2)/_components/WalletListSelector";
import { SOLANA_CHAIN_IDS } from "@/lib/chains";
import { filterWalletsByChain } from "@/lib/utils/chain-family";
import { toTurnkeyWallet } from "@/lib/utils/wallet";
import { useWalletsStore } from "@/stores/walletsStore";
import { useEffect, useMemo } from "react";
import { useRequiredTokens } from "./RequiredTokensContext";
interface WalletSelectorInputProps {
  label?: string;
  description?: string;
  headerRight?: React.ReactNode;
  value: string;
  onChange: (value: string) => void;
  /** Optional chain ID to filter wallets by chain family (EVM vs Solana). */
  chainId?: number;
}

/**
 * Wallet selector form field for choosing a Turnkey wallet.
 * Renders the user's organization wallets using WalletListSelector.
 * Value is the wallet ID string.
 */
export default function WalletSelectorInput({
  label,
  description,
  headerRight,
  value,
  onChange,
  chainId
}: WalletSelectorInputProps) {
  const {
    wallets: orgWallets,
    loading,
    loadWallets
  } = useWalletsStore();
  const requiredTokens = useRequiredTokens();

  // Load wallets on mount — the store handles org-change cache invalidation internally
  useEffect(() => {
    loadWallets();
  }, [loadWallets]);
  const allWallets = useMemo(() => orgWallets.map(toTurnkeyWallet), [orgWallets]);
  const wallets = useMemo(() => filterWalletsByChain(allWallets, chainId), [allWallets, chainId]);

  // Derive the selected address from the wallet ID value (use filtered list to avoid stale EVM/Solana mismatch)
  const selectedAddress = useMemo(() => {
    if (!value) return undefined;
    const wallet = wallets.find(w => w.walletId === value);
    return wallet?.address;
  }, [value, wallets]);
  const handleSelect = (address: string) => {
    const wallet_0 = wallets.find(w_0 => w_0.address === address);
    if (wallet_0?.walletId) {
      onChange(wallet_0.walletId);
    }
  };
  const isSolanaChain = chainId !== undefined && SOLANA_CHAIN_IDS.has(chainId);
  const emptyStateMessage = isSolanaChain ? "No Solana wallets found." : chainId ? "No compatible wallets found." : "No wallets found.";
  return <div className="space-y-2">
      {label && <div className="flex items-center justify-between gap-3">
          <label className="field-label block">{label}</label>
          {headerRight}
        </div>}
      {description && <p className="text-ds-text-subtle dark:text-ds-text-disabled text-xs">{description}</p>}
      <WalletListSelector wallets={wallets} selectedAddress={selectedAddress} onSelect={handleSelect} isLoading={loading} variant="compact" showCreateLink={true} emptyStateMessage={emptyStateMessage} requiredTokens={requiredTokens} />
    </div>;
}